import { Component, Input, Output, EventEmitter, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BatchService } from '../../services/batch.service';
import { RdsModule } from '@rabobank/rds-angular';
import { HttpEvent } from '@angular/common/http';
import { HttpEventType } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-upload-batch',
  standalone: true,
  imports: [CommonModule, FormsModule, RdsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <rds-button-circular-link class="rds-display-inline-block" icon="rds-icon-box-arrow-up" size="xs" variant="primary" (click)="presentModal()">Upload batch</rds-button-circular-link>

    <rds-modal id="upload-modal" [presented]="showModal" (rdsModalDidDismiss)="dismissModal()">
      <span slot="title">Upload batch</span>

      <div slot="body">
        <div class="rds-input-wrapper" *ngIf="requiresDocumentTypeSelection()">
          <rds-select
            id="pfDocumentType"
            label="Document Type"
            name="pfDocumentType"
            placeholder="Select document type"
            [(ngModel)]="selectedDocumentType"
            (ngModelChange)="onDocumentTypeChange($event)">
            <rds-option *ngFor="let dt of pfDocumentTypeOptions" [value]="dt">{{ dt }}</rds-option>
          </rds-select>
        </div>

        <div class="rds-input-wrapper">
          <rds-input-label>
            <label slot="label" class="upload-label">
              Upload file(s)
            </label>
          </rds-input-label>
          <div class="selection-hint" *ngIf="requiresDocumentTypeSelection() && !selectedDocumentType">
            Select a document type before uploading files.
          </div>
          <rds-input-file
            *ngIf="showModal && !uploading && (!requiresDocumentTypeSelection() || !!selectedDocumentType)"
            [inputInstruction]="uploadInstruction"
            button-label="Choose files"
            drop-area-title="Drag your files here"
            multiple
            (rdsInput)="onRdsInput($event)"
            (rdsChange)="onRdsInput($event)"
            (rdsClear)="onRdsInput($event)">
          </rds-input-file>

        <div class="selected-list" *ngIf="files && files.length > 0">
          <!-- Compact summary when not uploading: avoid duplicating the rds-input-file's own preview -->
          <div class="file-summary" *ngIf="!uploading">
            {{ files.length }} file{{ files.length === 1 ? '' : 's' }} selected
          </div>

          <!-- Per-file progress shown only during upload -->
          <ul *ngIf="uploading">
            <li *ngFor="let f of files; let i = index">
              <div class="file-row">
                <div class="file-name">{{ f.name }}</div>
                <div class="file-progress">
                  <rds-progress-bar [percentage]="progress[i] || 0" [hideLabel]="true">
                    <span slot="header-right">{{ getStatusText(i) }}</span>
                  </rds-progress-bar>
                </div>
              </div>
            </li>
          </ul>
        </div>
        </div>
        <div *ngIf="error" class="error">{{ error }}</div>

        <!-- uploader email is inferred from login; no user input required -->
      </div>

      <div slot="footer" class="modal-actions">
          <rds-button
          variant="primary"
          [disabled]="isUploadDisabled()"
          (click)="upload()">
          Upload
        </rds-button>

      </div>
    </rds-modal>
  `,
  styles: [
    `
      .rds-input-wrapper { margin-top: 0.6rem; }
      .drop-area {
        border: 2px dashed #d1d5db;
        border-radius: 6px;
        padding: 2rem;
        text-align: center;
        margin-bottom: 1rem;
      }
      .drop-area.dragover {
        background: #f8fafc;
        border-color: #93c5fd;
      }
      .drop-inner .icon {
        font-size: 2.5rem;
      }
      .upload-icon {
        display: inline-block;
        color: var(--rds-color-primary, #2563eb);
        margin-bottom: 0.5rem;
      }
      .muted {
        color: #6b7280;
        margin-bottom: 0.5rem;
      }
      .selected-list ul {
        list-style: none;
        padding: 0;
        margin: 0.5rem 0 0 0;
      }
      .selected-list li {
        padding: 0.35rem 0;
        border-bottom: 1px solid #eef2ff;
      }
      .file-row {
        display: flex;
        align-items: center;
        gap: 1rem;
      }
      .file-name {
        width: 320px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .file-progress {
        flex: 1;
      }
      .uploader-row { margin-top: 1rem; }
      .error { color: #dc3545; margin-top: 0.5rem; }
      .upload-label { display: block; margin-bottom: 0.5rem; }
    `,
  ],
})
export class UploadBatchComponent {
  private static readonly DEFAULT_MAX_FILES = 50;
  private static readonly PFEU_MAX_FILES = 8;
  readonly pfDocumentTypeOptions: string[] = [
    'Operational Report',
    'Credit Application Report',
  ];

  @Input() connector?: string;
  @Output() uploaded: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() uploadStatus: EventEmitter<'progress' | 'completed' | 'failed'> =
    new EventEmitter<'progress' | 'completed' | 'failed'>();

  files: File[] = [];
  error = '';
  statuses: string[] = []; // 'pending' | 'uploading' | 'success' | 'failed'
  // uploaderEmail removed; MSAL is used to infer the current user email
  uploading = false;
  showModal = false;
  progress: number[] = [];
  selectedDocumentType = '';

  constructor(
    private batchService: BatchService,
    private msalService: MsalService,
    private toastService: ToastService,
  ) {}

  get maxFiles(): number {
    return this.isPfeuConnector()
      ? UploadBatchComponent.PFEU_MAX_FILES
      : UploadBatchComponent.DEFAULT_MAX_FILES;
  }

  get uploadInstruction(): string {
    return `Max. ${this.maxFiles} files, only in PDF format.`;
  }

  public presentModal(): void {
    this.showModal = true;
    this.resetSelection();
  }

  public dismissModal(): void {
    if (this.uploading) {
      this.showModal = true;
      return;
    }
    this.showModal = false;
    this.resetSelection();
  }

  private resetSelection(): void {
    this.resetFileSelection();
    this.selectedDocumentType = '';
  }

  private resetFileSelection(): void {
    this.files = [];
    this.progress = [];
    this.statuses = [];
    this.error = '';
    this.uploading = false;
  }
  public onRdsInput(ev: unknown): void {
    // rds-input-file emits when files are selected/dropped. Try to extract FileList/files
    const eventRecord = ev as Record<string, unknown>;
    const payload = (eventRecord['detail'] as Record<string, unknown> | undefined) ?? eventRecord;
    const payloadFiles = payload['files'] as FileList | File[] | undefined;
    const payloadTarget = payload['target'] as { files?: FileList | File[] } | undefined;
    const list: FileList | File[] | undefined = payloadFiles || payloadTarget?.files;
    this.handleSelectedFiles(list);
  }

  private isPfeuConnector(): boolean {
    const normalized = (this.connector || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    return normalized.includes('PFEU') || (normalized.startsWith('PF') && normalized.includes('EU'));
  }

  public requiresDocumentTypeSelection(): boolean {
    return this.isPfeuConnector();
  }

  public onDocumentTypeChange(value: string): void {
    this.selectedDocumentType = value || '';
    this.resetFileSelection();
  }

  private getSelectionError(files: File[]): string {
    const invalid = files.filter((file) => !file.name.toLowerCase().endsWith('.pdf'));

    if (invalid.length > 0) {
      return `The following files are not accepted: ${invalid
        .map((file) => file.name)
        .join(', ')}. Only PDF files are allowed.`;
    }

    if (files.length > this.maxFiles) {
      return this.uploadInstruction;
    }

    return '';
  }

  private handleSelectedFiles(list: FileList | File[] | null | undefined): void {
    if (!list || list.length === 0) {
      this.resetFileSelection();
      return;
    }

    const incoming = Array.from(list as File[]);
    const selectionError = this.getSelectionError(incoming);

    if (selectionError) {
      this.files = [];
      this.progress = [];
      this.statuses = [];
      this.error = selectionError;
      return;
    }

    this.files = incoming;
    this.error = '';
    this.progress = this.files.map(() => 0);
    this.statuses = this.files.map(() => 'pending');
  }


  public removeFile(index: number): void {
    this.files.splice(index, 1);
    this.progress.splice(index, 1);
    this.statuses.splice(index, 1);
  }

  public isUploadDisabled(): boolean {
    return this.files.length === 0 || this.uploading || !!this.error || (this.requiresDocumentTypeSelection() && !this.selectedDocumentType);
  }

  public upload(): void {
    if (this.isUploadDisabled()) return;

    const selectionError = this.getSelectionError(this.files);
    if (selectionError) {
      this.error = selectionError;
      return;
    }

    // try to infer uploader email from MSAL if not provided
    let email = '';
    if (!email) {
      try {
        const active = this.msalService.instance.getActiveAccount();
        const claims =
          ((active as { idTokenClaims?: Record<string, unknown> } | null)?.idTokenClaims) || {};
        const claimEmail = claims['email'];
        const claimPreferredUsername = claims['preferred_username'];
        const claimUpn = claims['upn'];
        email =
          (typeof claimEmail === 'string' ? claimEmail : '') ||
          (typeof claimPreferredUsername === 'string' ? claimPreferredUsername : '') ||
          (typeof claimUpn === 'string' ? claimUpn : '') ||
          active?.username ||
          '';
      } catch (e) {
        email = '';
      }
    }

    if (!email) {
      this.toastService.show('error', 'Email missing', 'Please provide uploader email.');
      return;
    }
    this.uploading = true;
    this.uploadStatus.emit('progress');
    this.error = '';
    this.progress = this.files.map(() => 0);
    this.statuses = this.files.map(() => 'pending');

    const metadata: Record<string, string> = { connector: this.connector || '' };
    const documentType = this.requiresDocumentTypeSelection() && this.selectedDocumentType
      ? this.selectedDocumentType
      : undefined;

    // Upload files sequentially so we can report per-file progress and failures
    const uploadFile = (index: number, file: File) =>
      new Promise<void>((resolve) => {
        this.statuses[index] = 'uploading';
        const sub = this.batchService.uploadDocument([file], email, metadata, documentType).subscribe({
          next: (ev: unknown) => {
                const httpEvent = ev as HttpEvent<unknown>;
                if (httpEvent.type === HttpEventType.UploadProgress) {
                  const progressEvent = httpEvent as { loaded?: number; total?: number };
                  const loaded = progressEvent.loaded || 0;
                  const total = progressEvent.total || file.size || 0;
              const pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
              this.progress[index] = Math.min(100, Math.max(0, pct));
            }
                if (httpEvent.type === HttpEventType.Response) {
              this.progress[index] = 100;
              this.statuses[index] = 'success';
              sub.unsubscribe();
              resolve();
            }
          },
          error: (err: unknown) => {
            console.error('Upload failed for file', file.name, err);
            this.statuses[index] = 'failed';
            this.progress[index] = 0;
            sub.unsubscribe();
            resolve(); // continue with next files even if this one failed
          },
        });
      });

    (async () => {
      for (let i = 0; i < this.files.length; i++) {
        await uploadFile(i, this.files[i]);
      }
      // All done
      this.uploading = false;
      localStorage.setItem('lastUploadedBy', this.formatEmailToName(email));
      // this.toastService.show('success', 'Upload complete', 'Upload operation finished');
      // Close modal and emit if at least one succeeded
      const anySuccess = this.statuses.some((s) => s === 'success');
      if (anySuccess) {
        const succeededFiles = this.files
          .filter((_, idx) => this.statuses[idx] === 'success')
          .map((f) => f.name);
        this.uploadStatus.emit('completed');
        // Emit list of successful filenames so parent can update UI immediately
        this.uploaded.emit(succeededFiles);
        this.dismissModal();
      } else {
        this.uploadStatus.emit('failed');
      }
    })();
  }

  private updatePerFileProgress(loadedBytes: number): void {
    // Deprecated when uploading files sequentially. Kept for backward compatibility.
    if (!this.files || this.files.length === 0) return;
    let remaining = loadedBytes;
    const newProgress: number[] = [];
    for (let i = 0; i < this.files.length; i++) {
      const sz = this.files[i].size || 0;
      const allocated = Math.max(0, Math.min(sz, remaining));
      const pct = sz > 0 ? Math.round((allocated / sz) * 100) : 0;
      newProgress.push(pct);
      remaining = Math.max(0, remaining - allocated);
    }
    this.progress = newProgress;
  }

  public getStatusText(index: number): string {
    const s = this.statuses[index] || 'pending';
    if (s === 'uploading') return `${this.progress[index] || 0}%`;
    if (s === 'success') return 'Complete';
    if (s === 'failed') return 'Failed';
    return '';
  }

  private formatEmailToName(email: string): string {
  if (!email) return '';

  const namePart = email.split('@')[0];           // dharani.gottimukkula
  const pieces = namePart.split(/[._]/);          // ["dharani","gottimukkula"]

  return pieces
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))   // capitalize
    .join(' ');                                // "Dharani Gottimukkula"
}
}
